/**
 * Pure selectors for auto-host voice mode (#91), derived from the authoritative,
 * role-filtered {@link GameState} the client holds — never from transient per-socket
 * events (one truth, no event/state race; DESIGN §5.3, mirroring `selectors.ts`).
 *
 * Every selector is a no-op in `HUMAN` games (returns the "nothing to do" value), so
 * a human-hosted game renders byte-for-byte as before.
 */

import { gamePhaseOf } from '@jeopardy/shared';
import type { DisputeState, GameState } from '@jeopardy/shared';

/** True when this game is running in auto-host (voice) mode. */
export function isAutoHost(state: GameState | null): boolean {
  return state?.hostMode === 'AUTO';
}

/**
 * The clue the auto-host is currently reading aloud (status `READING`), or null.
 * The client synths this clue's `speech`/prompt and reports `read_complete` when done.
 */
export function clueBeingRead(state: GameState | null): string | null {
  if (!isAutoHost(state) || !state) return null;
  const cs = state.jeopardy.clueState;
  if (!cs || cs.status !== 'READING') return null;
  return cs.clueId;
}

/**
 * True when it is THIS player's turn to speak an answer: an AUTO game where they hold
 * the buzzer during the answer window (a buzz winner, or a Daily Double answerer).
 * When a player wins the buzz — or a DD answer window opens — the server sets
 * `clueState.status = 'LOCKED'` with `activePlayerId` = that player (the answer
 * window; `BUZZER_OPEN` is the pre-buzz window with NO active player), so THAT is the
 * "answer now" state. Human mode always returns false, so the UI never shows there.
 */
export function isMySpokenAnswerTurn(
  state: GameState | null,
  playerId: string | null,
): boolean {
  if (!isAutoHost(state) || !state || playerId === null) return false;
  const cs = state.jeopardy.clueState;
  if (!cs) return false;
  return cs.activePlayerId === playerId && cs.status === 'LOCKED';
}

/** True when the auto-host is waiting on the LLM verdict for a submitted answer. */
export function isJudging(state: GameState | null): boolean {
  if (!isAutoHost(state) || !state) return false;
  return state.jeopardy.clueState?.status === 'JUDGING';
}

/**
 * The clue whose CORRECT ANSWER the auto-host should read aloud (#192, FR-4.10), or
 * null. Set during an UNANSWERED end-of-clue reveal (`status === 'RESOLVED'`,
 * `reveal.outcome === 'unanswered'`) in an AUTO game once the server has un-blanked
 * the response. The client derives the answer's speech segments from the revealed
 * response and speaks them (same Polly-backed synthesizer as clue reading). A
 * correct-award reveal returns null (the answer is never read aloud). Human mode
 * always returns null.
 */
export function answerBeingRead(state: GameState | null): string | null {
  if (!isAutoHost(state) || !state) return null;
  const cs = state.jeopardy.clueState;
  if (!cs || cs.status !== 'RESOLVED' || cs.reveal?.outcome !== 'unanswered') return null;
  const response = state.jeopardy.packSnapshot.cluesById[cs.clueId]?.response ?? '';
  return response.length > 0 ? cs.clueId : null;
}

/**
 * The Final Peril correct response the auto-host should read aloud (#205, FR-6.6),
 * or null. Non-null in an AUTO game while `phase ∈ {FINAL_JUDGING, FINAL_REVEAL}`
 * once the server has un-blanked the Final response — the sibling of
 * {@link answerBeingRead} for the board-clue reveal. The client derives the answer's
 * speech segments from the revealed response and speaks them once (same Polly-backed
 * synthesizer as clue reading). Human mode always returns null (a human host speaks).
 */
export function finalAnswerBeingRead(state: GameState | null): string | null {
  if (!isAutoHost(state) || !state) return null;
  if (gamePhaseOf(state) !== 'FINAL_JUDGING' && gamePhaseOf(state) !== 'FINAL_REVEAL')
    return null;
  const response = state.jeopardy.packSnapshot.finalJeopardy.response ?? '';
  return response.length > 0 ? response : null;
}

/** Accept (keep-ruling) / reject (overturn) counts of the cast votes. */
export function disputeTally(dispute: DisputeState): { accept: number; reject: number } {
  let accept = 0;
  let reject = 0;
  for (const keep of Object.values(dispute.votes)) {
    if (keep) accept++;
    else reject++;
  }
  return { accept, reject };
}

/**
 * What the Dispute UI should render for this viewer:
 *  - `none`     — nothing to show (human game, or no ruling to dispute yet);
 *  - `canOpen`  — a ruling stands and this player MAY open a dispute vote;
 *  - `voting`   — a dispute vote is open (render the tally, plus accept/reject for an
 *                 eligible voter).
 */
export interface DisputeView {
  mode: 'none' | 'canOpen' | 'voting';
  /** The clue whose ruling is targeted (for the command payload). */
  clueId?: string;
  /** Voting only: whether this viewer may cast a vote. */
  canVote: boolean;
  /** Voting only: this viewer's current vote (accept=true / reject=false), if cast. */
  myVote?: boolean;
  /** Voting only: whether this viewer is the ruled (excluded) player. */
  isRuledPlayer: boolean;
  /** Voting only: the live tally. */
  tally?: { accept: number; reject: number };
}

export function disputeView(
  state: GameState | null,
  playerId: string | null,
): DisputeView {
  const none: DisputeView = { mode: 'none', canVote: false, isRuledPlayer: false };
  if (!isAutoHost(state) || !state) return none;

  const dispute = state.jeopardy.dispute;
  if (dispute) {
    const canVote = playerId !== null && dispute.eligibleVoterIds.includes(playerId);
    return {
      mode: 'voting',
      clueId: dispute.clueId,
      canVote,
      myVote: playerId !== null ? dispute.votes[playerId] : undefined,
      isRuledPlayer: playerId !== null && playerId === dispute.ruledPlayerId,
      tally: disputeTally(dispute),
    };
  }

  const ruling = state.jeopardy.lastAutoRuling;
  if (ruling && playerId !== null) {
    return { ...none, mode: 'canOpen', clueId: ruling.clueId };
  }
  return none;
}
